import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box, ButtonBase, IconButton, Typography, Paper, Zoom,
  CircularProgress, Badge, Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import SendIcon from "@mui/icons-material/Send";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import NotesIcon from "@mui/icons-material/Notes";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import api from "../../services/api";
import {
  OXB, FONTE, AvatarBot, AvatarBotMini, PontoOnline, BotaoTopo, LinkVoltar, TituloTela,
  PilulaAcao, PilulaStatus, CampoChamado, CaixaEntrada, BotaoEnviar,
} from "./ui";

const WELCOME_MSG = {
  role: "assistant",
  content: "Olá! 👋 Sou o **OxeBot**, assistente virtual da plataforma OxeTech. Como posso te ajudar hoje?\n\nVocê pode me perguntar sobre:\n• Como se inscrever em turmas\n• Como acessar aulas e materiais\n• Problemas de login ou conta\n• E muito mais!\n\nSe eu não conseguir resolver, posso abrir um chamado para nossa equipe. 😊",
};

// Na tela, a lista de temas da boas-vindas vira os botões de SUGESTOES logo abaixo.
// O texto completo (WELCOME_MSG) continua sendo o que vai no histórico para a API
// e para o chamado — aqui muda só o que aparece no balão.
const WELCOME_EXIBIDA = "Olá! 👋 Sou o **OxeBot**, assistente virtual da plataforma OxeTech. Como posso te ajudar hoje?\n\nSe eu não conseguir resolver, posso abrir um chamado para nossa equipe. 😊";

// Temas da boas-vindas como botões. Clicar envia a pergunta pelo fluxo normal do chat
// (sendMessage). Inscrição e login caem no FAQ instantâneo do backend (AiChatController);
// Work é coberto pelo FAQ/prompt do mesmo controller.
const SUGESTOES = [
  { emoji: "🎓", texto: "Como me inscrevo em uma turma?" },
  { emoji: "📚", texto: "Onde acesso aulas e materiais?" },
  { emoji: "🔑", texto: "Problemas de login ou conta" },
  { emoji: "💼", texto: "Como funcionam as vagas do Work?" },
];

// Padrões de saudação para responder localmente sem chamar a API
const GREETING_PATTERNS = /^(ol[aá]|oi|hey|hello|hi|bom\s*dia|boa\s*tarde|boa\s*noite|e\s*a[ií]|fala|salve|opa)[!?.,\s]*$/i;

const GREETING_RESPONSES = [
  "Olá! 👋 Que bom te ver por aqui! Sou o **OxeBot**, assistente virtual da plataforma OxeTech. Como posso te ajudar hoje?",
  "Oi! 😊 Eu sou o **OxeBot** e estou aqui para te ajudar! Pode me perguntar sobre turmas, aulas, materiais ou qualquer dúvida sobre a plataforma.",
  "Olá! 👋 Tudo bem? Sou o **OxeBot**, seu assistente virtual. Me conta, no que posso te ajudar?",
  "Oi! 🤖 Prazer em falar com você! Sou o **OxeBot** da OxeTech. Como posso ser útil?",
];

const getGreetingResponse = () => {
  return GREETING_RESPONSES[Math.floor(Math.random() * GREETING_RESPONSES.length)];
};

/* ── Animações do balão de boas-vindas, do botão flutuante e do "pensando" ── */
const thoughtBubbleStyles = `
@keyframes oxebot-float-in {
  0% { opacity: 0; transform: translateY(10px) scale(0.92); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes oxebot-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
@keyframes oxebot-pulse-ring {
  0% { box-shadow: 0 0 0 0 rgba(196,86,31,0.3); }
  70% { box-shadow: 0 0 0 12px rgba(196,86,31,0); }
  100% { box-shadow: 0 0 0 0 rgba(196,86,31,0); }
}
@keyframes oxebot-typing-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-5px); opacity: 1; }
}
@keyframes oxebot-thinking-fade-in {
  0% { opacity: 0; transform: scale(0.9); }
  100% { opacity: 1; transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .oxebot-anim { animation: none !important; }
}
`;

/* Componente Typing Dots — bolinhas animadas dentro do balão */
const TypingDots = () => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: "8px", py: "2px",
    animation: "oxebot-thinking-fade-in 0.3s ease-out",
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          className="oxebot-anim"
          sx={{
            width: 7, height: 7, borderRadius: "50%",
            bgcolor: OXB.laranja,
            animation: `oxebot-typing-bounce 1.4s ease-in-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
    </Box>
    <Typography sx={{ fontSize: 12, color: OXB.apagado, fontFamily: FONTE, fontStyle: "italic" }}>
      pensando...
    </Typography>
  </Box>
);

const STATUS_LABELS = {
  aberto: { label: "Aberto", color: OXB.laranja, bg: OXB.pessego },
  em_andamento: { label: "Em andamento", color: OXB.azul, bg: OXB.azulClaro },
  resolvido: { label: "Resolvido", color: OXB.verde, bg: OXB.verdeClaro },
  fechado: { label: "Fechado", color: OXB.apagado, bg: "#F1EDE6" },
};

// Datas vindas da API podem chegar nulas ou inválidas — nunca deixam a tela quebrar.
const formatarData = (valor, modo = "data") => {
  try {
    if (!valor) return "";
    const d = new Date(valor);
    if (Number.isNaN(d.getTime())) return "";
    if (modo === "completa") return d.toLocaleString("pt-BR");
    if (modo === "curta") return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("pt-BR");
  } catch {
    return "";
  }
};

// "agora" no primeiro minuto; depois, a hora (HH:mm) em que a mensagem apareceu.
const rotuloHora = (ts, agoraMs) => {
  try {
    if (!ts) return "";
    if (agoraMs - ts < 60000) return "agora";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

// Chip do topo da conversa: "Hoje" ou a data em que ela começou.
const rotuloDia = (ts) => {
  try {
    if (!ts) return "Hoje";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "Hoje";
    return d.toDateString() === new Date().toDateString() ? "Hoje" : d.toLocaleDateString("pt-BR");
  } catch {
    return "Hoje";
  }
};

const historicoDe = (ticket) => (Array.isArray(ticket?.historico_chat) ? ticket.historico_chat : []);

// Barra de rolagem fina, no tom creme
const sxRolagem = {
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: `${OXB.begeBorda} transparent`,
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": { bgcolor: OXB.begeBorda, borderRadius: 6 },
};

function OxeBot() {
  const authToken = useSelector((state) => state.auth?.token);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketData, setTicketData] = useState({ nome: "", email: "", telefone: "", titulo: "", descricao: "" });
  const [ticketSent, setTicketSent] = useState(false);
  const [ticketError, setTicketError] = useState("");
  const [unread, setUnread] = useState(0);
  const [showThoughtBubble, setShowThoughtBubble] = useState(false);
  const [thoughtDismissed, setThoughtDismissed] = useState(false);
  // Meus Chamados
  const [showMyTickets, setShowMyTickets] = useState(false);
  const [myTickets, setMyTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState("");
  const [ticketAnexo, setTicketAnexo] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);
  const messagesEndRef = useRef(null);
  const ticketChatEndRef = useRef(null);
  const inputRef = useRef(null);

  // ── Drag & Drop state ──
  const [fabPos, setFabPos] = useState({ bottom: 24, right: 24 });

  // Etapa atual do raciocinio ("Consultando suas turmas...") ou posicao na fila do
  // Spark. O ref evita re-render a cada token quando o rotulo nao mudou.
  const [fase, setFase] = useState("");
  const faseRef = useRef("");
  const mudarFase = (txt) => {
    if (faseRef.current === txt) return;
    faseRef.current = txt;
    setFase(txt);
  };
  useEffect(() => {
    if (!loading && faseRef.current) { faseRef.current = ""; setFase(""); }
  }, [loading]);
  const dragRef = useRef({
    isDragging: false,
    hasMoved: false,
    startX: 0,
    startY: 0,
    startRight: 24,
    startBottom: 24,
  });
  const fabRef = useRef(null);
  const DRAG_THRESHOLD = 6; // px mínimo para considerar arraste vs clique

  const handleDragStart = useCallback((clientX, clientY) => {
    dragRef.current = {
      isDragging: true,
      hasMoved: false,
      startX: clientX,
      startY: clientY,
      startRight: fabPos.right,
      startBottom: fabPos.bottom,
    };
  }, [fabPos]);

  const handleDragMove = useCallback((clientX, clientY) => {
    const d = dragRef.current;
    if (!d.isDragging) return;
    const dx = d.startX - clientX;
    const dy = d.startY - clientY;
    if (!d.hasMoved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
    d.hasMoved = true;
    const newRight = Math.max(8, Math.min(window.innerWidth - 85, d.startRight + dx));
    const newBottom = Math.max(8, Math.min(window.innerHeight - 85, d.startBottom + dy));
    setFabPos({ right: newRight, bottom: newBottom });
  }, []);

  const handleDragEnd = useCallback(() => {
    dragRef.current.isDragging = false;
  }, []);

  // Mouse events
  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  }, [handleDragStart]);

  useEffect(() => {
    const onMouseMove = (e) => handleDragMove(e.clientX, e.clientY);
    const onMouseUp = () => handleDragEnd();
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [handleDragMove, handleDragEnd]);

  // Touch events
  const onTouchStart = useCallback((e) => {
    const t = e.touches[0];
    handleDragStart(t.clientX, t.clientY);
  }, [handleDragStart]);

  useEffect(() => {
    const onTouchMove = (e) => {
      if (!dragRef.current.isDragging) return;
      const t = e.touches[0];
      handleDragMove(t.clientX, t.clientY);
    };
    const onTouchEnd = () => handleDragEnd();
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [handleDragMove, handleDragEnd]);

  const handleFabClick = useCallback(() => {
    // Só abre o chat se NÃO arrastou
    if (dragRef.current.hasMoved) return;
    setShowThoughtBubble(false);
    setThoughtDismissed(true);
    setOpen(true);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (open) { setUnread(0); inputRef.current?.focus(); setShowThoughtBubble(false); }
  }, [open]);

  // Carregar meus chamados
  const loadMyTickets = useCallback(async () => {
    if (!authToken) return;
    setLoadingTickets(true);
    try {
      const { data } = await api.get("/support-tickets/my");
      setMyTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao carregar meus chamados:", err);
      setMyTickets([]);
    }
    setLoadingTickets(false);
  }, [authToken]);

  const handleOpenMyTickets = useCallback(() => {
    setShowMyTickets(true);
    setSelectedTicket(null);
    loadMyTickets();
  }, [loadMyTickets]);

  const handleSelectTicket = useCallback(async (ticket) => {
    // Buscar detalhes completos do ticket
    try {
      const { data } = await api.get(`/support-tickets/${ticket.id}`);
      setSelectedTicket(data);
    } catch {
      setSelectedTicket(ticket);
    }
    setTicketReply("");
  }, []);

  const handleSendTicketReply = useCallback(async () => {
    const textoTrim = (ticketReply || "").trim();
    if ((!textoTrim && !ticketAnexo) || !selectedTicket || sendingReply) return;
    setSendingReply(true);
    try {
      let data;
      if (ticketAnexo) {
        const fd = new FormData();
        fd.append("mensagem", textoTrim);
        fd.append("arquivo", ticketAnexo);
        const res = await api.post(`/support-tickets/${selectedTicket.id}/user-reply`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        data = res.data;
      } else {
        const res = await api.post(`/support-tickets/${selectedTicket.id}/user-reply`, {
          mensagem: textoTrim,
        });
        data = res.data;
      }
      setSelectedTicket(data);
      setTicketReply("");
      setTicketAnexo(null);
      // Atualizar a lista
      loadMyTickets();
    } catch (err) {
      console.error("Erro ao enviar resposta:", err);
    }
    setSendingReply(false);
  }, [ticketReply, ticketAnexo, selectedTicket, sendingReply, loadMyTickets]);

  useEffect(() => {
    if (ticketChatEndRef.current) {
      ticketChatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedTicket?.historico_chat]);

  // Mostrar thought bubble após 3 segundos se o chat não estiver aberto
  useEffect(() => {
    if (open || thoughtDismissed) return;
    const timer = setTimeout(() => {
      setShowThoughtBubble(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [open, thoughtDismissed]);

  // Auto-esconder thought bubble após 12 segundos
  useEffect(() => {
    if (!showThoughtBubble) return;
    const timer = setTimeout(() => {
      setShowThoughtBubble(false);
      setThoughtDismissed(true);
    }, 12000);
    return () => clearTimeout(timer);
  }, [showThoughtBubble]);

  // textoPronto vem dos botões de sugestão; sem ele, envia o que foi digitado.
  const sendMessage = async (textoPronto) => {
    const veioDoInput = typeof textoPronto !== "string";
    const text = (veioDoInput ? input : textoPronto).trim();
    if (!text || loading) return;

    const userMsg = { role: "user", content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    // Sugestão clicada não apaga o rascunho que a pessoa estiver digitando
    if (veioDoInput) setInput("");

    // Interceptar saudações simples — responder localmente sem chamar API
    if (GREETING_PATTERNS.test(text)) {
      const localReply = getGreetingResponse();
      setTimeout(() => {
        setMessages((prev) => [...prev, { role: "assistant", content: localReply }]);
      }, 400);
      return;
    }

    setLoading(true);

    // Adicionar placeholder "pensando" imediatamente para feedback visual
    setMessages((prev) => [...prev, { role: "assistant", content: "", _thinking: true }]);

    try {
      const chatHistory = newMessages
        .filter((m) => m.role !== "system")
        .map((m) => ({ role: m.role, content: m.content }));

      // Usar fetch com streaming SSE para resposta em tempo real
      // Remove barra final para evitar "//ai-chat" (que o navegador interpreta
      // como URL protocol-relative → host inexistente "ai-chat" → Failed to fetch).
      // Mesmo tratamento que o interceptor do axios (services/api.js) já aplica.
      const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
      const fetchHeaders = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      };
      if (authToken) {
        fetchHeaders["Authorization"] = `Bearer ${authToken}`;
      }
      const response = await fetch(`${baseUrl}/ai-chat`, {
        method: "POST",
        headers: fetchHeaders,
        body: JSON.stringify({ messages: chatHistory }),
      });

      if (!response.ok) {
        // Remover placeholder e mostrar erro
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: "assistant", content: "Ops! Houve um problema na comunicação. Tente novamente em instantes.", _error: true };
          return updated;
        });
        setLoading(false);
        return;
      }

      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("text/event-stream")) {
        // Modo streaming — acumular tokens em tempo real
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let streamedContent = "";
        let shouldOpenTicket = false;
        let sseBuffer = "";

        setLoading(false);

        const processLine = (line) => {
          if (!line.startsWith("data: ")) return;
          const data = line.slice(6);
          if (data === "[DONE]") return;
          try {
            const parsed = JSON.parse(data);
            // event: fase — etapa do raciocinio ou posicao na fila
            if (parsed.rotulo) {
              mudarFase(parsed.rotulo);
            }
            if (parsed.token) {
              streamedContent += parsed.token;
              // Limpar tags internas que o usuário não deve ver
              const cleaned = streamedContent
                .replace("[ABRIR_CHAMADO]", "")
                .replace(/\[ACTION:\w+:\d+\]/g, "")
                .trim();
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: cleaned };
                return updated;
              });
            }
            // Resultado de ação transacional executada pelo backend
            if (parsed.action_result) {
              setMessages((prev) => [
                ...prev,
                { role: "assistant", content: parsed.message || "Ação processada." },
              ]);
            }
            if (parsed.done && parsed.should_open_ticket) {
              shouldOpenTicket = true;
            }
            if (parsed.error) {
              streamedContent = parsed.message || "Ops! Houve um problema na comunicação. Tente novamente em instantes.";
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: streamedContent, _error: true };
                return updated;
              });
            }
          } catch {
            // ignora JSON inválido
          }
        };

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          sseBuffer += decoder.decode(value, { stream: true });
          const lines = sseBuffer.split("\n");
          sseBuffer = lines.pop() || "";
          for (const line of lines) {
            processLine(line.trim());
          }
        }
        // Processar buffer restante
        if (sseBuffer.trim()) processLine(sseBuffer.trim());

        if (shouldOpenTicket) {
          setTimeout(() => setShowTicketForm(true), 1000);
        }
        if (!open) setUnread((u) => u + 1);
      } else {
        // Fallback: resposta JSON normal
        const data = await response.json();
        let reply = data?.message || "Desculpe, não consegui entender. Pode reformular?";
        const shouldOpenTicket = data?.should_open_ticket || false;
        reply = reply.replace("[ABRIR_CHAMADO]", "").trim();
        // Substituir placeholder pelo conteúdo real
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: "assistant", content: reply };
          return updated;
        });
        if (shouldOpenTicket) {
          setTimeout(() => setShowTicketForm(true), 1000);
        }
        if (!open) setUnread((u) => u + 1);
        setLoading(false);
      }
    } catch (err) {
      console.error("OxeBot erro:", err);
      // Substituir placeholder pela mensagem de erro
      setMessages((prev) => {
        const updated = [...prev];
        const lastMsg = updated[updated.length - 1];
        if (lastMsg?.role === "assistant" && (!lastMsg.content || lastMsg._thinking)) {
          updated[updated.length - 1] = { role: "assistant", content: "Ops! Houve um problema na comunicação. Tente novamente em instantes.", _error: true };
        } else {
          updated.push({ role: "assistant", content: "Ops! Houve um problema na comunicação. Tente novamente em instantes." });
        }
        return updated;
      });
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const submitTicket = async () => {
    if (!ticketData.nome || !ticketData.email || !ticketData.telefone || !ticketData.titulo || !ticketData.descricao) {
      setTicketError("Preencha todos os campos obrigatórios (nome, e-mail, telefone, título e descrição).");
      return;
    }
    setTicketError("");
    try {
      // Filtra mensagens de erro do bot — não devem ir pro histórico do chamado
      const chatHistory = messages
        .filter((m) => !m._error && !m._thinking)
        .map((m) => ({ role: m.role, content: (m.content || "").slice(0, 500) }));
      await api.post("/support-tickets", {
        ...ticketData,
        categoria: "duvida",
        historico_chat: chatHistory,
      });
      setTicketSent(true);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "✅ Chamado aberto com sucesso! Nossa equipe vai analisar e retornar em breve. Obrigado pela paciência!" },
      ]);
      setTimeout(() => { setShowTicketForm(false); setTicketSent(false); setTicketData({ nome: "", email: "", telefone: "", titulo: "", descricao: "" }); }, 3000);
    } catch {
      setTicketError("Erro ao enviar chamado. Tente novamente.");
    }
  };

  const renderMarkdown = (text) => {
    if (!text) return "";
    // Escapa HTML ANTES de aplicar a formatação — msg.content pode conter
    // <script>/<img onerror=...>. Sem isso, o dangerouslySetInnerHTML abaixo
    // executaria o payload (XSS). Remediação incidente StealC.
    const escaped = String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return escaped
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/\n/g, "<br/>")
      .replace(/• /g, "&#8226; ");
  };

  /* ── Apoio visual (não muda regra nenhuma) ── */

  // Pergunta escolhida no balão de boas-vindas: só é enviada depois que o chat abre,
  // para o sendMessage já enxergar open=true (senão o contador de não lidas sobe).
  const [perguntaPendente, setPerguntaPendente] = useState(null);
  useEffect(() => {
    if (!open || !perguntaPendente) return;
    const texto = perguntaPendente;
    setPerguntaPendente(null);
    sendMessage(texto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, perguntaPendente]);

  // Hora em que cada mensagem apareceu na tela (por índice; as mensagens só crescem
  // até o "fechar", que zera). Alimenta o "agora"/HH:mm e o chip "Hoje".
  const horaMsgRef = useRef([]);
  if (open) {
    messages.forEach((_, i) => {
      if (!horaMsgRef.current[i]) horaMsgRef.current[i] = Date.now();
    });
  }
  // Relógio de 30 s para o "agora" virar HH:mm com o chat aberto
  const [agoraMs, setAgoraMs] = useState(() => Date.now());
  useEffect(() => {
    if (!open) return undefined;
    setAgoraMs(Date.now());
    const id = setInterval(() => setAgoraMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, [open]);

  const abrirDoBalao = () => { setShowThoughtBubble(false); setThoughtDismissed(true); setOpen(true); };
  const fecharChat = () => { setOpen(false); setMessages([WELCOME_MSG]); horaMsgRef.current = []; };
  const voltarAoChat = () => { setShowMyTickets(false); setSelectedTicket(null); };
  const abrirNovoChamado = () => { setShowMyTickets(false); setSelectedTicket(null); setShowTicketForm(true); };

  // Mesma condição da validação do submitTicket — só pinta o botão; o clique continua
  // passando pelo submitTicket, que mostra a mensagem de campos obrigatórios.
  const chamadoCompleto = !!(ticketData.nome && ticketData.email && ticketData.telefone && ticketData.titulo && ticketData.descricao);
  const telaChamado = showTicketForm && !ticketSent;

  const sxBalaoBot = {
    bgcolor: "#fff",
    color: OXB.tinta,
    borderRadius: "18px 18px 18px 6px",
    border: `1px solid ${OXB.linha}`,
    boxShadow: "0 2px 8px rgba(70,40,10,.05)",
  };
  const sxBalaoUsuario = {
    bgcolor: OXB.laranja,
    color: "#fff",
    borderRadius: "18px 18px 6px 18px",
    boxShadow: "0 4px 12px rgba(196,86,31,.2)",
  };

  return (
    <>
      {/* Injetar CSS das animações */}
      <style>{thoughtBubbleStyles}</style>

      {/* ──── BALÃO DE BOAS-VINDAS (perto do botão flutuante) ──── */}
      {showThoughtBubble && !open && (
        <Box
          className="oxebot-anim"
          onClick={abrirDoBalao}
          sx={{
            position: "fixed",
            bottom: fabPos.bottom + 86,
            right: fabPos.right,
            zIndex: 10000,
            width: 312,
            maxWidth: "calc(100vw - 32px)",
            cursor: "pointer",
            animation: "oxebot-float-in 0.5s ease-out forwards, oxebot-float 3s ease-in-out 0.5s infinite",
            fontFamily: FONTE,
          }}
        >
          <Box sx={{
            position: "relative",
            bgcolor: "#fff",
            borderRadius: "20px",
            p: "16px",
            boxShadow: "0 14px 36px rgba(70,40,10,.16), 0 2px 6px rgba(70,40,10,.06)",
          }}>
            {/* Cabeçalho: avatar + nome + online */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", pr: "30px" }}>
              <AvatarBot tamanho={42} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: FONTE, fontWeight: 700, fontSize: 15, color: OXB.tinta, lineHeight: 1.25 }}>
                  OxeBot
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mt: "2px" }}>
                  <PontoOnline tamanho={7} />
                  <Typography sx={{ fontFamily: FONTE, fontSize: 12, fontWeight: 500, color: OXB.online, lineHeight: 1.2 }}>
                    Online agora
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* X cinza redondo no canto */}
            <ButtonBase
              aria-label="Fechar"
              title="Fechar"
              onClick={(e) => { e.stopPropagation(); setShowThoughtBubble(false); setThoughtDismissed(true); }}
              sx={{
                position: "absolute", top: 12, right: 12,
                width: 26, height: 26, borderRadius: "50%",
                bgcolor: OXB.bege, color: OXB.apagado,
                transition: "background-color .15s, color .15s",
                "&:hover": { bgcolor: OXB.begeBorda, color: OXB.tinta },
              }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </ButtonBase>

            <Typography sx={{ fontFamily: FONTE, fontSize: 14, color: OXB.tinta, lineHeight: 1.5, mt: "12px" }}>
              Oi! 👋 Precisa de ajuda com inscrição, aulas ou sua conta?
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mt: "14px" }}>
              <ButtonBase
                onClick={(e) => {
                  e.stopPropagation();
                  setPerguntaPendente(SUGESTOES[0].texto);
                  abrirDoBalao();
                }}
                sx={{
                  fontFamily: FONTE, fontSize: 13, fontWeight: 700, lineHeight: 1.2,
                  px: "13px", py: "8px", borderRadius: 99,
                  bgcolor: OXB.pessego, color: OXB.laranja, border: `1px solid ${OXB.pessegoBorda}`,
                  transition: "border-color .15s",
                  "&:hover": { borderColor: OXB.laranja },
                }}
              >
                Como me inscrevo?
              </ButtonBase>
              <ButtonBase
                onClick={(e) => { e.stopPropagation(); abrirDoBalao(); }}
                sx={{
                  fontFamily: FONTE, fontSize: 13, fontWeight: 600, lineHeight: 1.2,
                  px: "13px", py: "8px", borderRadius: 99,
                  bgcolor: "#fff", color: OXB.tinta, border: `1px solid ${OXB.borda}`,
                  transition: "border-color .15s",
                  "&:hover": { borderColor: OXB.apagado },
                }}
              >
                Conversar
              </ButtonBase>
            </Box>

            {/* Ponta do balão apontando para o botão flutuante */}
            <Box aria-hidden="true" sx={{
              position: "absolute", bottom: -6, right: 30,
              width: 14, height: 14, bgcolor: "#fff", transform: "rotate(45deg)",
              borderRadius: "0 0 3px 0",
              boxShadow: "4px 4px 8px rgba(70,40,10,.06)",
            }} />
          </Box>
        </Box>
      )}

      {/* FAB — arrastável */}
      <Zoom in={!open}>
        <Box
          ref={fabRef}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          onClick={handleFabClick}
          aria-label="Abrir o OxeBot"
          sx={{
            position: "fixed",
            bottom: fabPos.bottom,
            right: fabPos.right,
            zIndex: 9999,
            width: 71, height: 71,
            borderRadius: "50%",
            cursor: dragRef.current.isDragging ? "grabbing" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden",
            transition: dragRef.current.isDragging ? "none" : "transform 0.2s ease, box-shadow 0.2s ease",
            animation: showThoughtBubble ? "oxebot-pulse-ring 2s infinite" : "none",
            bgcolor: "#ffffff",
            boxShadow: "0 6px 20px rgba(70,40,10,.18)",
            "&:hover": { transform: "scale(1.08)", boxShadow: "0 8px 26px rgba(196,86,31,.42)" },
            // Forçar fundo branco mesmo com color-scheme dark
            colorScheme: "light",
            WebkitAppearance: "none",
          }}
        >
          <Badge badgeContent={unread} color="error" overlap="circular"
            sx={{ "& .MuiBadge-badge": { top: 6, right: 6 } }}
          >
            <video
              autoPlay loop muted playsInline
              src="/assets/oxebot-icon.webm"
              style={{
                width: 60, height: 60, borderRadius: "50%", objectFit: "cover",
                backgroundColor: "#ffffff",
              }}
            />
          </Badge>
        </Box>
      </Zoom>

      {/* ──── JANELA DO CHAT ──── */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={0}
          role="dialog"
          aria-label="OxeBot, assistente virtual da OxeTech"
          sx={{
            position: "fixed",
            bottom: fabPos.bottom,
            right: fabPos.right,
            zIndex: 9999,
            width: { xs: "calc(100vw - 32px)", sm: 380 },
            height: { xs: "72vh", sm: 600 },
            maxHeight: "80vh",
            borderRadius: "24px", display: "flex", flexDirection: "column",
            overflow: "hidden",
            bgcolor: OXB.fundo,
            fontFamily: FONTE,
            boxShadow: "0 24px 60px rgba(60,30,5,.22), 0 4px 14px rgba(60,30,5,.08)",
          }}
        >
          {/* Cabeçalho em degradê laranja */}
          <Box sx={{
            position: "relative", overflow: "hidden", flexShrink: 0,
            background: `linear-gradient(135deg, ${OXB.laranja} 0%, ${OXB.laranjaFim} 100%)`,
            p: "16px 16px 16px 18px", display: "flex", alignItems: "center", gap: "12px",
          }}>
            {/* Círculo decorativo translúcido à direita */}
            <Box aria-hidden="true" sx={{
              position: "absolute", right: -46, top: -58, width: 160, height: 160,
              borderRadius: "50%", bgcolor: "rgba(255,255,255,.12)", pointerEvents: "none",
            }} />
            <AvatarBot tamanho={44} borda />
            <Box sx={{ flex: 1, minWidth: 0, position: "relative" }}>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 17, fontFamily: FONTE, lineHeight: 1.25 }}>
                OxeBot
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "2px" }}>
                <PontoOnline cor={OXB.pontoOnlineClaro} tamanho={7} halo="rgba(142,240,181,.25)" />
                <Typography sx={{ color: "rgba(255,255,255,.9)", fontSize: 12.5, fontFamily: FONTE, lineHeight: 1.2 }}>
                  Online · responde na hora
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: "8px", position: "relative" }}>
              <BotaoTopo rotulo="Minimizar" onClick={() => setOpen(false)}>
                <RemoveIcon sx={{ fontSize: 18 }} />
              </BotaoTopo>
              <BotaoTopo rotulo="Fechar" onClick={fecharChat}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </BotaoTopo>
            </Box>
          </Box>

          {telaChamado ? (
            /* ──── TELA ABRIR CHAMADO DE SUPORTE ──── */
            <Box sx={{ flex: 1, ...sxRolagem, p: "14px 18px 18px" }}>
              <LinkVoltar onClick={() => setShowTicketForm(false)}>← Voltar ao chat</LinkVoltar>
              <TituloTela>Abrir chamado de suporte</TituloTela>
              <Typography sx={{ fontFamily: FONTE, fontSize: 13, color: OXB.suave, mt: "4px", mb: "16px", lineHeight: 1.5 }}>
                Nossa equipe vai analisar e retornar em breve.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <CampoChamado
                  icone={<PersonOutlineIcon />}
                  placeholder="Seu nome"
                  inputProps={{ "aria-label": "Seu nome (obrigatório)" }}
                  value={ticketData.nome}
                  onChange={(e) => setTicketData({ ...ticketData, nome: e.target.value })}
                />
                <CampoChamado
                  icone={<MailOutlineIcon />}
                  type="email"
                  placeholder="E-mail"
                  inputProps={{ "aria-label": "E-mail (obrigatório)" }}
                  value={ticketData.email}
                  onChange={(e) => setTicketData({ ...ticketData, email: e.target.value })}
                />
                <CampoChamado
                  icone={<PhoneOutlinedIcon />}
                  placeholder="Telefone / WhatsApp"
                  inputProps={{ "aria-label": "Telefone ou WhatsApp (obrigatório)" }}
                  value={ticketData.telefone}
                  onChange={(e) => setTicketData({ ...ticketData, telefone: e.target.value })}
                />
                <CampoChamado
                  icone={<NotesIcon />}
                  placeholder="Título do problema"
                  inputProps={{ "aria-label": "Título do problema (obrigatório)" }}
                  value={ticketData.titulo}
                  onChange={(e) => setTicketData({ ...ticketData, titulo: e.target.value })}
                />
                <CampoChamado
                  multiline
                  minRows={3}
                  maxRows={8}
                  placeholder="Descreva o que aconteceu"
                  inputProps={{ "aria-label": "Descreva o que aconteceu (obrigatório)" }}
                  value={ticketData.descricao}
                  onChange={(e) => setTicketData({ ...ticketData, descricao: e.target.value })}
                />
                {ticketError && (
                  <Box role="alert" sx={{
                    fontFamily: FONTE, fontSize: 12.5, fontWeight: 500, lineHeight: 1.45,
                    color: OXB.vermelho, bgcolor: OXB.vermelhoClaro, borderRadius: "12px", p: "9px 12px",
                  }}>
                    {ticketError}
                  </Box>
                )}
                <ButtonBase
                  onClick={submitTicket}
                  sx={{
                    width: "100%", py: "13px", mt: "4px", borderRadius: "14px", gap: "8px",
                    fontFamily: FONTE, fontSize: 15, fontWeight: 700,
                    bgcolor: chamadoCompleto ? OXB.laranja : OXB.desabilitado,
                    color: chamadoCompleto ? "#fff" : OXB.desabilitadoTexto,
                    boxShadow: chamadoCompleto ? "0 8px 18px rgba(196,86,31,.25)" : "none",
                    transition: "background-color .15s, box-shadow .15s",
                    "&:hover": { bgcolor: chamadoCompleto ? OXB.laranjaHover : OXB.desabilitado },
                  }}
                >
                  <SendIcon sx={{ fontSize: 17 }} />
                  Enviar chamado
                </ButtonBase>
              </Box>
            </Box>
          ) : showMyTickets ? (
            /* ──── TELA MEUS CHAMADOS ──── */
            !selectedTicket ? (
              /* Lista de chamados */
              <Box sx={{ flex: 1, ...sxRolagem, p: "14px 18px 18px" }}>
                <LinkVoltar onClick={voltarAoChat}>← Voltar ao chat</LinkVoltar>
                <TituloTela>Meus chamados</TituloTela>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mt: "14px" }}>
                  {loadingTickets ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress size={24} sx={{ color: OXB.laranja }} />
                    </Box>
                  ) : myTickets.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 3 }}>
                      <Box sx={{
                        width: 52, height: 52, borderRadius: "50%", bgcolor: OXB.bege, mx: "auto", mb: "10px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <ConfirmationNumberIcon sx={{ fontSize: 26, color: OXB.apagado }} />
                      </Box>
                      <Typography sx={{ fontFamily: FONTE, fontSize: 13.5, color: OXB.suave }}>
                        {authToken ? "Você ainda não tem chamados." : "Faça login para ver seus chamados."}
                      </Typography>
                    </Box>
                  ) : (
                    myTickets.map((t) => {
                      const st = STATUS_LABELS[t?.status] || STATUS_LABELS.aberto;
                      const hasNewAdmin = historicoDe(t).some((m) => m?.role === "admin");
                      return (
                        <ButtonBase
                          key={t?.id}
                          onClick={() => handleSelectTicket(t)}
                          sx={{
                            display: "block", width: "100%", textAlign: "left",
                            bgcolor: "#fff", borderRadius: "14px",
                            border: `1px solid ${OXB.borda}`, borderLeft: `4px solid ${st.color}`,
                            p: "12px 14px",
                            transition: "border-color .15s, background-color .15s",
                            "&:hover": { borderColor: OXB.pessegoBorda, borderLeftColor: st.color, bgcolor: OXB.realce },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Typography sx={{
                              flex: 1, minWidth: 0, fontFamily: FONTE, fontWeight: 700, fontSize: 14, color: OXB.tinta,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              #{t?.id} — {t?.titulo}
                            </Typography>
                            <PilulaStatus st={st} />
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px", mt: "6px" }}>
                            <Typography sx={{ fontFamily: FONTE, fontSize: 12, color: OXB.apagado }}>
                              {formatarData(t?.created_at, "data")}
                            </Typography>
                            {hasNewAdmin && (
                              <Box component="span" sx={{
                                px: "8px", py: "2px", borderRadius: 99, bgcolor: OXB.verdeClaro, color: OXB.verde,
                                fontFamily: FONTE, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                              }}>
                                Resposta do suporte
                              </Box>
                            )}
                          </Box>
                        </ButtonBase>
                      );
                    })
                  )}
                  <ButtonBase
                    onClick={abrirNovoChamado}
                    sx={{
                      width: "100%", py: "12px", borderRadius: "14px",
                      border: `1.5px dashed ${OXB.pessegoBorda}`, bgcolor: OXB.realce,
                      color: OXB.laranja, fontFamily: FONTE, fontSize: 13.5, fontWeight: 700,
                      transition: "border-color .15s, background-color .15s",
                      "&:hover": { borderColor: OXB.laranja, bgcolor: OXB.pessego },
                    }}
                  >
                    + Abrir novo chamado
                  </ButtonBase>
                </Box>
              </Box>
            ) : (
              /* Chat do chamado selecionado */
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <Box sx={{
                  flexShrink: 0, px: "18px", pt: "12px", pb: "6px",
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px",
                }}>
                  <LinkVoltar onClick={() => setSelectedTicket(null)}>← Meus chamados</LinkVoltar>
                  <LinkVoltar discreto onClick={voltarAoChat}>Voltar ao chat</LinkVoltar>
                </Box>

                <Box sx={{ flex: 1, ...sxRolagem, px: "18px", pb: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                  {/* Info do ticket */}
                  {(() => {
                    const st = STATUS_LABELS[selectedTicket?.status] || STATUS_LABELS.aberto;
                    return (
                      <Box sx={{
                        bgcolor: "#fff", borderRadius: "14px", p: "12px 14px", mb: "4px",
                        border: `1px solid ${OXB.borda}`, borderLeft: `4px solid ${st.color}`,
                      }}>
                        <Typography sx={{ fontFamily: FONTE, fontSize: 12, fontWeight: 600, color: OXB.apagado }}>
                          Chamado #{selectedTicket?.id}
                        </Typography>
                        <Typography sx={{ fontFamily: FONTE, fontSize: 15, fontWeight: 700, color: OXB.tinta, lineHeight: 1.35, mt: "2px" }}>
                          {selectedTicket?.titulo}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px", mt: "6px" }}>
                          <PilulaStatus st={st} tamanho={11} />
                          <Typography sx={{ fontFamily: FONTE, fontSize: 12, color: OXB.apagado }}>
                            {formatarData(selectedTicket?.created_at || selectedTicket?.createdAt, "completa")}
                          </Typography>
                        </Box>
                        {selectedTicket?.descricao && (
                          <Typography sx={{ fontFamily: FONTE, fontSize: 13, color: OXB.suave, mt: "8px", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                            {selectedTicket.descricao}
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}

                  {/* Mensagens do chamado */}
                  {historicoDe(selectedTicket).length === 0 && (
                    <Typography sx={{ fontFamily: FONTE, color: OXB.apagado, fontSize: 12.5, textAlign: "center", py: 2 }}>
                      Aguardando resposta da equipe de suporte...
                    </Typography>
                  )}
                  {historicoDe(selectedTicket).map((msg, i) => {
                    const isUser = msg?.role === "user";
                    const isAdmin = msg?.role === "admin";
                    const isBot = msg?.role === "assistant";
                    return (
                      <Box key={i} sx={{
                        display: "flex", flexDirection: "column",
                        alignItems: isUser ? "flex-end" : "flex-start",
                      }}>
                        <Typography sx={{
                          fontFamily: FONTE, fontSize: 11, fontWeight: 700, mb: "3px", px: "4px",
                          color: isAdmin ? OXB.verde : isBot ? OXB.laranja : OXB.apagado,
                        }}>
                          {isAdmin ? "🎧 Suporte" : isBot ? "🤖 OxeBot" : "Você"}
                        </Typography>
                        <Box sx={{
                          maxWidth: "86%",
                          ...(isUser ? sxBalaoUsuario : sxBalaoBot),
                          ...(isAdmin ? { bgcolor: OXB.verdeClaro, border: `1px solid ${OXB.verdeBorda}`, boxShadow: "none" } : {}),
                          p: "9px 13px",
                          fontSize: 13.5,
                          lineHeight: 1.5,
                          fontFamily: FONTE,
                          wordBreak: "break-word",
                          "& strong": { fontWeight: 700 },
                        }}>
                          {msg?.content && (
                            <span dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                          )}
                          {msg?.attachment && (
                            msg.attachment.mime_type?.startsWith("image/") ? (
                              <Box component="a" href={msg.attachment.url} target="_blank" rel="noopener noreferrer"
                                sx={{ display: "block", mt: msg.content ? 0.75 : 0, borderRadius: "10px", overflow: "hidden", maxWidth: 220 }}>
                                <Box component="img" src={msg.attachment.url} alt={msg.attachment.filename_original}
                                  sx={{ width: "100%", display: "block", cursor: "zoom-in" }} />
                              </Box>
                            ) : (
                              <Box component="a" href={msg.attachment.url} target="_blank" rel="noopener noreferrer"
                                sx={{
                                  display: "inline-flex", alignItems: "center", gap: 0.5,
                                  mt: msg.content ? 0.75 : 0, px: "10px", py: "5px", borderRadius: "10px",
                                  bgcolor: isUser ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.04)",
                                  border: "1px solid " + (isUser ? "rgba(255,255,255,0.3)" : OXB.borda),
                                  textDecoration: "none", color: "inherit", maxWidth: 220, fontSize: 12,
                                }}>
                                📎 <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>
                                  {msg.attachment.filename_original}
                                </span>
                              </Box>
                            )
                          )}
                          {msg?.timestamp && (
                            <Typography sx={{ fontFamily: FONTE, fontSize: 10.5, color: isUser ? "rgba(255,255,255,0.7)" : OXB.apagado, mt: "3px", textAlign: "right" }}>
                              {formatarData(msg.timestamp, "curta")}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  <div ref={ticketChatEndRef} />
                </Box>

                {/* Resposta do usuário */}
                {selectedTicket?.status !== "fechado" && (
                  <Box sx={{ flexShrink: 0, p: "10px 14px 14px", borderTop: `1px solid ${OXB.borda}`, bgcolor: "#fff" }}>
                    {ticketAnexo && (
                      <Box sx={{
                        display: "flex", alignItems: "center", gap: "8px", mb: "8px", p: "6px 8px",
                        borderRadius: "12px", bgcolor: OXB.realce, border: `1px dashed ${OXB.laranja}`,
                      }}>
                        {ticketAnexo.type?.startsWith("image/") ? (
                          <Box component="img" src={URL.createObjectURL(ticketAnexo)} alt="preview"
                            sx={{ width: 28, height: 28, objectFit: "cover", borderRadius: "6px" }} />
                        ) : (
                          <Box sx={{ fontSize: 14 }}>📎</Box>
                        )}
                        <Typography sx={{ flex: 1, fontFamily: FONTE, fontSize: 12, color: OXB.tinta, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {ticketAnexo.name}
                        </Typography>
                        <IconButton size="small" aria-label="Remover anexo" onClick={() => setTicketAnexo(null)} sx={{ width: 24, height: 24, color: OXB.apagado }}>
                          <CloseIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
                      <IconButton component="label" disabled={sendingReply} title="Anexar imagem/PDF (até 10MB)" aria-label="Anexar imagem ou PDF"
                        sx={{
                          width: 42, height: 42, flexShrink: 0, bgcolor: OXB.bege, color: OXB.suave,
                          "&:hover": { bgcolor: OXB.begeBorda },
                        }}>
                        <AttachFileIcon sx={{ fontSize: 19, transform: "rotate(45deg)" }} />
                        <input
                          type="file"
                          hidden
                          accept="image/*,application/pdf"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            if (f.size > 10 * 1024 * 1024) { alert("Arquivo excede 10MB."); e.target.value = ""; return; }
                            setTicketAnexo(f);
                            e.target.value = "";
                          }}
                        />
                      </IconButton>
                      <CaixaEntrada
                        placeholder="Escreva uma mensagem..."
                        inputProps={{ "aria-label": "Escreva uma mensagem" }}
                        value={ticketReply}
                        onChange={(e) => setTicketReply(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendTicketReply(); } }}
                        onPaste={(e) => {
                          // Cole direto print da área de transferência (Ctrl+V).
                          const items = e.clipboardData?.items;
                          if (!items) return;
                          for (let i = 0; i < items.length; i++) {
                            const it = items[i];
                            if (it.kind === "file" && it.type?.startsWith("image/")) {
                              const file = it.getAsFile();
                              if (file) {
                                if (file.size > 10 * 1024 * 1024) { alert("Imagem excede 10MB."); return; }
                                const ext = file.type.split("/")[1] || "png";
                                const renamed = new File([file], `print_${new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19)}.${ext}`, { type: file.type });
                                setTicketAnexo(renamed);
                                e.preventDefault();
                                return;
                              }
                            }
                          }
                        }}
                        disabled={sendingReply}
                      />
                      <BotaoEnviar
                        ativo={(!!ticketReply.trim() || !!ticketAnexo) && !sendingReply}
                        carregando={sendingReply}
                        onClick={handleSendTicketReply}
                        rotulo="Enviar resposta"
                      />
                    </Box>
                  </Box>
                )}
                {selectedTicket?.status === "fechado" && (
                  <Box sx={{ flexShrink: 0, p: "12px 14px", borderTop: `1px solid ${OXB.borda}`, bgcolor: OXB.bege, textAlign: "center" }}>
                    <Typography sx={{ fontFamily: FONTE, fontSize: 12.5, color: OXB.apagado }}>
                      Este chamado foi fechado.
                    </Typography>
                  </Box>
                )}
              </Box>
            )
          ) : (
            /* ──── CHAT NORMAL DO OXEBOT ──── */
            <Box sx={{ flex: 1, ...sxRolagem, p: "14px 16px 10px", display: "flex", flexDirection: "column", gap: "8px" }}>
              <Box sx={{
                alignSelf: "center", px: "12px", py: "3px", borderRadius: 99, mb: "2px",
                bgcolor: OXB.bege, color: OXB.suave, fontFamily: FONTE, fontSize: 11.5, fontWeight: 600,
              }}>
                {rotuloDia(horaMsgRef.current[0])}
              </Box>

              {messages.map((msg, idx) => {
                const isUser = msg?.role === "user";
                const isThinking = msg?._thinking && !msg?.content;
                const ehBoasVindas = msg === WELCOME_MSG;
                const proxima = messages[idx + 1];
                // Avatar + hora só no fim de cada sequência do mesmo autor
                const fimDoBloco = !proxima || proxima?.role !== msg?.role;
                return (
                  <React.Fragment key={idx}>
                    <Box sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start" }}>
                      <Box sx={{
                        maxWidth: "86%",
                        ...(isUser ? sxBalaoUsuario : sxBalaoBot),
                        p: "10px 14px",
                        fontSize: 14,
                        fontFamily: FONTE,
                        lineHeight: 1.55,
                        wordBreak: "break-word",
                        "& strong": { fontWeight: 700 },
                        minWidth: isThinking ? 150 : "auto",
                      }}>
                        {isThinking ? <TypingDots /> : (
                          <span dangerouslySetInnerHTML={{ __html: renderMarkdown(ehBoasVindas ? WELCOME_EXIBIDA : msg?.content) }} />
                        )}
                      </Box>
                    </Box>

                    {fimDoBloco && (
                      <Box sx={{
                        display: "flex", alignItems: "center", gap: "6px", px: "2px", mt: "-2px",
                        justifyContent: isUser ? "flex-end" : "flex-start",
                      }}>
                        {!isUser && <AvatarBotMini tamanho={22} />}
                        <Typography sx={{ fontFamily: FONTE, fontSize: 11.5, color: OXB.apagado }}>
                          {rotuloHora(horaMsgRef.current[idx], agoraMs)}
                        </Typography>
                      </Box>
                    )}

                    {ehBoasVindas && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: "4px", mb: "2px" }}>
                        {SUGESTOES.map((s) => (
                          <ButtonBase
                            key={s.texto}
                            onClick={() => sendMessage(s.texto)}
                            disabled={loading}
                            sx={{
                              width: "100%", justifyContent: "flex-start", gap: "10px", textAlign: "left",
                              p: "10px 14px", borderRadius: "14px",
                              bgcolor: "#fff", border: `1px solid ${OXB.pessegoBorda}`,
                              fontFamily: FONTE, fontSize: 13.5, fontWeight: 600, color: OXB.tinta, lineHeight: 1.35,
                              transition: "border-color .15s, background-color .15s",
                              "&:hover, &.Mui-focusVisible": { borderColor: OXB.laranja, bgcolor: OXB.realce },
                              "&.Mui-disabled": { opacity: 0.55 },
                            }}
                          >
                            <Box component="span" aria-hidden="true" sx={{ fontSize: 17, lineHeight: 1, flexShrink: 0 }}>
                              {s.emoji}
                            </Box>
                            {s.texto}
                          </ButtonBase>
                        ))}
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}

              {loading && fase && (
                <Typography sx={{
                  fontSize: 11.5, color: OXB.apagado, fontStyle: "italic",
                  fontFamily: FONTE, px: "4px", lineHeight: 1.4,
                }}>
                  {fase}
                </Typography>
              )}
              <div ref={messagesEndRef} />
            </Box>
          )}

          {/* Rodapé do chat: atalhos de chamado + campo de mensagem */}
          {!showMyTickets && !telaChamado && (
            <Box sx={{ flexShrink: 0, p: "10px 14px 14px", borderTop: `1px solid ${OXB.borda}`, bgcolor: "#fff" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: "10px" }}>
                <PilulaAcao tom="laranja" onClick={() => setShowTicketForm(true)} title="Abrir chamado">
                  <span aria-hidden="true">🎧</span> Abrir chamado
                </PilulaAcao>
                {authToken && (
                  <PilulaAcao tom="bege" onClick={handleOpenMyTickets} title="Meus chamados">
                    <span aria-hidden="true">🎫</span> Meus chamados
                  </PilulaAcao>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
                <CaixaEntrada
                  inputRef={inputRef}
                  placeholder="Digite sua dúvida..."
                  inputProps={{ "aria-label": "Digite sua dúvida" }}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                />
                <BotaoEnviar
                  ativo={!!input.trim() && !loading}
                  onClick={() => sendMessage()}
                  rotulo="Enviar mensagem"
                />
              </Box>
            </Box>
          )}
        </Paper>
      </Slide>
    </>
  );
}

export default OxeBot;
